import {
    Component,
    inject,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {
    DrawerService,
    LayoutModule,
    LayoutService,
    MAIN_URL,
    ModalService,
    SharedUiModule,
} from '@pf/shared-ui';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
    resolveDocumentationTarget,
    toDocumentationEmbedUrl,
} from '@cpw/domain';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { first, map, of, switchMap, tap } from 'rxjs';
import { NativeLogoutService } from '@pf/shared/ui-platform';
import {
    PlatformWmsDomainModule,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import {
    PlatformAuthService,
    ProfileProvider,
    SafeAny,
    SelectOption,
} from '@pf/shared-common';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'cpw-app-header',
    imports: [LayoutModule, SharedUiModule, PlatformWmsDomainModule],
    templateUrl: './cpw-app-header.component.html',
    styleUrl: './cpw-app-header.component.scss',
    providers: [],
})
export class CpwAppHeaderComponent implements OnInit {
    private readonly modalService = inject(ModalService);
    private readonly authService = inject(PlatformAuthService);
    private readonly nativeLogout = inject(NativeLogoutService);
    private readonly mainUrl = inject(MAIN_URL);
    private readonly drawerService = inject(DrawerService);
    private readonly router = inject(Router);
    private readonly sanitizer = inject(DomSanitizer);
    readonly positionsFacade = inject(UserPositionFacade);
    private readonly profileProvider = inject(ProfileProvider);
    layoutService = inject(LayoutService);
    warehouseFacade = inject(WarehouseFacade);
    warehouseControl = new FormControl<string | null>(null);
    selectedWarehouse?: SelectOption | null;

    @ViewChild('warehouseDrawerContent', { static: true })
    warehouseDrawerContent!: TemplateRef<SafeAny>;
    @ViewChild('userDrawerContent', { static: true })
    userDrawerContent!: TemplateRef<SafeAny>;
    @ViewChild('documentationEmbedContent', { static: true })
    documentationEmbedContent!: TemplateRef<SafeAny>;

    /** Sanitized embed URL for the documentation drawer (mobile only). */
    embedSafeUrl?: SafeResourceUrl;

    private warehouseDrawerRef?: NzDrawerRef;

    private readonly supportEmail = 'ctsupport@firebend.com';
    isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    get supportMailtoLink(): string {
        const warehouseName =
            this.selectedWarehouse?.label ??
            this.warehouseFacade.active?.name ??
            '';
        const profile = this.profileProvider.profile;
        const userName = profile?.fullName ?? '';
        const userEmail = profile?.email ?? '';
        const user = [userName, userEmail].filter(Boolean).join(' - ');
        const currentUrl = (window as SafeAny).location?.href ?? '';

        const body = [
            'Hi Control Tower Support,',
            '',
            'I need help with the following:',
            '',
            '[Please describe your issue here]',
            '',
            '--- Context ---',
            `Warehouse: ${warehouseName}`,
            `User: ${user}`,
            `URL: ${currentUrl}`,
            '',
            '--- Helpful for troubleshooting ---',
            'Error Message (if shown): ',
            'Screenshot: [Paste or attach screenshot if possible]',
            '',
            'Thank you.',
        ].join('\n');

        return `mailto:${this.supportEmail}?subject=${encodeURIComponent(
            'Control Tower CPW - Support Request'
        )}&body=${encodeURIComponent(body)}`;
    }

    /**
     * Notion documentation page for the current screen, falling back to the
     * root docs page. Used as the desktop "Documentation" link target (opened
     * in a new tab). Evaluated when the account drawer is opened.
     */
    get documentationUrl(): string {
        return resolveDocumentationTarget(this.router.url).url;
    }

    ngOnInit(): void {
        this.warehouseFacade
            .searchAll$()
            .pipe(first())
            .subscribe(warehouses => {
                if (!warehouses.length) {
                    console.error('No warehouses assigned to user!');
                    return;
                }
                const warehouse = this.warehouseFacade.active ?? warehouses[0];
                const warehouseId = warehouse?.id;
                if (!this.warehouseFacade.active && warehouseId) {
                    // First login (or cleared storage): nothing has set the
                    // active warehouse yet. Everything downstream filters on
                    // it (e.g. queues observe active$), so default it to the
                    // first assigned warehouse instead of only updating the
                    // header display.
                    this.warehouseFacade.setActive(warehouseId);
                }
                this.warehouseControl.setValue(warehouseId || '');
                this.selectedWarehouse = warehouse
                    ? ({
                          value: warehouse.id,
                          label: warehouse.name,
                      } as SelectOption)
                    : null;
            });
    }

    preLogoutAction() {
        return this.positionsFacade.exitPosition$().pipe(
            first(),
            map(() => true)
        );
    }

    openWarehouseDrawer() {
        this.warehouseDrawerRef = this.drawerService.createTemplateDrawer({
            title: 'Switch Warehouse',
            content: this.warehouseDrawerContent,
            placement: 'top',
            height: 'auto',
            isMobile: this.isMobile,
            onClose: () => {
                this.warehouseDrawerRef = undefined;
            },
        });
    }

    openUserDrawer() {
        this.drawerService.createTemplateDrawer({
            title: 'Account',
            content: this.userDrawerContent,
            placement: 'top',
            height: 'auto',
            isMobile: this.isMobile,
        });
    }

    /**
     * Opens the documentation for the current screen in an in-app drawer.
     * Used on mobile (Android APK WebView), where opening a new browser tab is
     * not available. Desktop uses a plain `target="_blank"` link instead.
     */
    openDocumentationEmbed(drawerRef?: NzDrawerRef) {
        drawerRef?.close();
        const target = resolveDocumentationTarget(this.router.url);
        this.embedSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            toDocumentationEmbedUrl(target.id)
        );
        this.drawerService.createTemplateDrawer({
            title: 'Documentation',
            content: this.documentationEmbedContent,
            placement: 'top',
            height: '90dvh',
            isMobile: this.isMobile,
        });
    }

    selectWarehouse(option?: SelectOption) {
        if (!option) {
            return;
        }

        if (this.warehouseFacade.active?.id === option.value) {
            this.warehouseDrawerRef?.close();
            return;
        }

        if (this.positionsFacade.active) {
            const previousWarehouse = this.selectedWarehouse;
            this.modalService.warning({
                title: 'Switch Warehouse',
                content:
                    'Switching warehouses will exit your current position. Do you want to continue?',
                onOk: () => {
                    this.positionsFacade
                        .exitPosition$()
                        .pipe(
                            first(),
                            tap(() => {
                                this.selectedWarehouse = option;
                                this.warehouseFacade.setActive(option.value);
                                this.warehouseDrawerRef?.close();
                                (window as SafeAny).location.href = '/';
                            })
                        )
                        .subscribe();
                },
                onCancel: () => {
                    this.selectedWarehouse = previousWarehouse;
                    this.warehouseControl.setValue(
                        previousWarehouse?.value || ''
                    );
                },
            });
            return;
        }

        this.selectedWarehouse = option;
        this.warehouseFacade.setActive(option.value);
        this.warehouseDrawerRef?.close();
        (window as SafeAny).location.reload();
    }

    logout(drawerRef?: NzDrawerRef) {
        drawerRef?.close();
        this.positionsFacade
            .exitPosition$()
            .pipe(
                first(),
                switchMap(() => {
                    this.authService.logout(this.mainUrl);

                    if (this.nativeLogout.canUseNativeLogout) {
                        return this.nativeLogout.logout();
                    }

                    return of(true);
                })
            )
            .subscribe();
    }

    showHelpModal(drawerRef?: NzDrawerRef) {
        drawerRef?.close();
        this.modalService.info({
            title: 'Need help?',
            content: `Please email <strong>${this.supportEmail}</strong> with a description of your issue and we'll get back to you as soon as possible.`,
        });
    }
}
